import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import {
  Building,
  Heart,
  Cpu,
  Landmark,
  Zap,
  Factory,
  ArrowRight,
  CheckCircle,
  ShieldCheck,
} from "lucide-react";

const defaultIndustries = [
  {
    icon: Building,
    name: "Financial Services",
    desc: "Banking, insurance, and fintech organizations face stringent regulatory requirements. GACA helps financial institutions achieve ISO 27001, PCI DSS readiness, and SOC 2 compliance to protect sensitive financial data and maintain customer trust.",
    standards: ["ISO 27001", "SOC 2 Readiness", "PCI DSS", "ISO 22301"],
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80",
  },
  {
    icon: Heart,
    name: "Healthcare & Life Sciences",
    desc: "Healthcare organizations must protect patient data while maintaining operational efficiency. Our team helps achieve compliance with ISO 27001, ISO 13485, and supports HIPAA readiness assessments.",
    standards: ["ISO 27001", "ISO 13485", "ISO 9001", "HIPAA Readiness"],
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=600&q=80",
  },
  {
    icon: Cpu,
    name: "Technology & SaaS",
    desc: "Technology companies need to demonstrate security maturity to enterprise clients. We guide SaaS companies, software firms, and cloud providers through ISO 27001, SOC 2, and cloud security certifications.",
    standards: ["ISO 27001", "SOC 2 Readiness", "Cloud Security", "ISO 27701"],
    image: "https://images.unsplash.com/flagged/photo-1579274216947-86eaa4b00475?w=600&q=80",
  },
  {
    icon: Landmark,
    name: "Government & Defense",
    desc: "Public sector organizations require the highest levels of information security and compliance. GACA provides certification and assurance services aligned with government security frameworks.",
    standards: ["ISO 27001", "NIST CSF", "ISO 22301", "Risk Management"],
    image: "https://images.unsplash.com/photo-1555848962-6e79363ec58f?w=600&q=80",
  },
  {
    icon: Zap,
    name: "Energy & Utilities",
    desc: "Critical infrastructure operators face unique cybersecurity challenges. Our team helps energy companies protect operational technology systems while achieving ISO compliance.",
    standards: ["ISO 27001", "ISO 22301", "IEC 62443", "NIST CSF"],
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&q=80",
  },
  {
    icon: Factory,
    name: "Manufacturing",
    desc: "Manufacturing organizations must secure both IT and OT environments. GACA helps manufacturers achieve quality and security certifications to compete in global markets.",
    standards: ["ISO 9001", "ISO 27001", "ISO 14001", "ISO 45001"],
    image: "https://images.unsplash.com/photo-1764114908655-9a26d32750a0?w=600&q=80",
  },
];

export default function IndustriesPage() {
  const { t } = useLang();
  const ip = t?.industries_page || {};
  const tSectors = ip.sectors || [];
  
  // Merge translated content with default industries (icons, standards, images)
  const industries = defaultIndustries.map((def, i) => {
    const ts = tSectors[i];
    return {
      icon: def.icon,
      name: ts?.name || def.name,
      desc: ts?.desc || def.desc,
      standards: def.standards,
      image: def.image,
    };
  });
  return (
    <div className="page-transition pt-20" data-testid="industries-page">
      {/* Header */}
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{ip.badge || "Industries"}</p>
          <h1
            data-testid="industries-page-heading"
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {ip.title || "Sector Expertise"}
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            {ip.subtitle || "We understand that every industry has unique compliance requirements and security challenges."}
          </p>
        </div>
      </section>

      {/* Industries Detail */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0 border border-[#0B2545]/10">
            {industries.map((ind, i) => (
              <div
                key={ind.name}
                data-testid={`industry-detail-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-5 gap-0 border-b border-[#0B2545]/10 last:border-b-0 ${i % 2 === 1 ? 'bg-[#F5F7FA]' : 'bg-white'}`}
              >
                {/* Image */}
                <div className="lg:col-span-2 h-64 lg:h-auto">
                  <img src={ind.image} alt={ind.name} className="w-full h-full object-cover" />
                </div>
                {/* Content */}
                <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <ind.icon size={24} className="text-[#13A8BD]" strokeWidth={1.5} />
                    <h2 className="text-2xl font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {ind.name}
                    </h2>
                  </div>
                  <p className="text-base text-slate-600 leading-relaxed mb-6">{ind.desc}</p>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-3">Relevant Standards</h4>
                    <div className="flex flex-wrap gap-2">
                      {ind.standards.map((s) => (
                        <span key={s} className="text-xs px-3 py-1.5 border border-[#0B2545]/15 text-[#0B2545] font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#F5F7FA] border-t border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            Don't See Your Industry?
          </h2>
          <p className="text-base text-slate-600 max-w-xl mx-auto mb-10">
            Our certification services extend across all sectors. Contact us to discuss
            your industry-specific compliance requirements.
          </p>
          <Link
            to="/contact"
            data-testid="industries-cta"
            className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors"
          >
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
